import { Databases, Query } from 'appwrite';
import client from '../app-user';

const DATABASE_ID = process.env.REACT_APP_DATABASE_ID || 'database';
const POWER_RANKINGS_COLLECTION_ID = process.env.REACT_APP_POWER_RANKINGS_COLLECTION_ID || 'power-rankings';
const TEAMS_COLLECTION_ID = process.env.REACT_APP_TEAMS_COLLECTION_ID || 'teams';

type StoredRanking = {
  rosterId: string;
  teamId: string;
  projectedPoints: number;
  rank: number;
};

type RankingSnapshot = {
  rankingsJson: string;
};

type TeamDocument = {
  sleeperID: string;
  name: string;
};

export type PowerRanking = StoredRanking & {
  owner: string;
  avatarUrl: string | null;
  rankChange: number | null;
};

const databases = new Databases(client);
const normalize = (value?: string | null) => (value || '').trim().toLowerCase();

const parseRankings = (snapshot?: RankingSnapshot): StoredRanking[] => {
  if (!snapshot?.rankingsJson) return [];
  try {
    const rankings = JSON.parse(snapshot.rankingsJson);
    return Array.isArray(rankings) ? rankings : [];
  } catch {
    return [];
  }
};

const getAvatarUrl = async (sleeperID: string) => {
  try {
    const response = await fetch(`https://api.sleeper.app/v1/user/${encodeURIComponent(sleeperID)}`);
    if (!response.ok) return null;
    const user = await response.json() as { avatar?: string | null };
    return user.avatar ? `https://sleepercdn.com/avatars/thumbs/${user.avatar}` : null;
  } catch {
    return null;
  }
};

export const listPowerRankings = async (): Promise<PowerRanking[]> => {
  const [snapshotResult, teamResult] = await Promise.all([
    databases.listDocuments(DATABASE_ID, POWER_RANKINGS_COLLECTION_ID, [Query.orderDesc('weekOf'), Query.limit(2)]),
    databases.listDocuments(DATABASE_ID, TEAMS_COLLECTION_ID, [Query.limit(50)]),
  ]);

  const [currentSnapshot, previousSnapshot] = snapshotResult.documents as unknown as RankingSnapshot[];
  const currentRankings = parseRankings(currentSnapshot);
  const previousRankings = parseRankings(previousSnapshot);
  const previousRanks = new Map(previousRankings.map((ranking) => [normalize(ranking.teamId), ranking.rank]));
  const teams = teamResult.documents as unknown as TeamDocument[];
  const teamBySleeperId = new Map(teams.map((team) => [normalize(team.sleeperID), team]));
  const avatarEntries = await Promise.all(teams.map(async (team) => [normalize(team.sleeperID), await getAvatarUrl(team.sleeperID)] as const));
  const avatars = new Map(avatarEntries);

  return currentRankings.sort((a, b) => a.rank - b.rank).map((ranking) => {
    const teamKey = normalize(ranking.teamId);
    const previousRank = previousRanks.get(teamKey);
    const team = teamBySleeperId.get(teamKey);
    return {
      ...ranking,
      owner: team?.name || ranking.teamId,
      avatarUrl: avatars.get(teamKey) || null,
      rankChange: typeof previousRank === 'number' ? previousRank - ranking.rank : null,
    };
  });
};
